import { useState } from 'react';

/**
 * Custom hook for managing Additional Decision Support state
 */
const useAdditionalDecisionSupport = () => {
  const getDefaultAdditionalDecisionSupport = () => ({
    currentThreatSummary: { 
      twelveHours: '', 
      twentyFourHours: '', 
      fortyEightHours: '', 
      seventyTwoHours: '', 
      afterSeventyTwoHours: '' 
    },
    criticalResourceNeeds: { 
      twelveHours: '', 
      twentyFourHours: '', 
      fortyEightHours: '', 
      seventyTwoHours: '', 
      afterSeventyTwoHours: '' 
    },
    strategicDiscussions: '',
    plannedActionsNextPeriod: '',
    projectedFinalIncidentArea: '',
    anticipatedCompletionDate: '',
    projectedDemobilizationDate: '',
    estimatedCostsToDate: '',
    projectedFinalCosts: '',
    generalComments: ''
  });

  const [additionalDecisionSupportEntries, setAdditionalDecisionSupportEntries] = useState([]);
  const [currentAdditionalDecisionSupport, setCurrentAdditionalDecisionSupport] = useState(getDefaultAdditionalDecisionSupport());
  const [editingAdditionalDecisionSupport, setEditingAdditionalDecisionSupport] = useState(null);

  const updateCurrentThreatSummary = (key, value) => {
    setCurrentAdditionalDecisionSupport(prev => ({
      ...prev,
      currentThreatSummary: {
        ...prev.currentThreatSummary,
        [key]: value
      }
    }));
  };

  const updateCriticalResourceNeeds = (key, value) => {
    setCurrentAdditionalDecisionSupport(prev => ({
      ...prev,
      criticalResourceNeeds: {
        ...prev.criticalResourceNeeds,
        [key]: value
      }
    }));
  };

  const addAdditionalDecisionSupport = () => {
    if (editingAdditionalDecisionSupport) {
      setAdditionalDecisionSupportEntries(prev => prev.map(d => 
        d.id === editingAdditionalDecisionSupport.id 
          ? { ...currentAdditionalDecisionSupport, id: d.id, timestamp: d.timestamp, updatedAt: new Date().toISOString() }
          : d
      ));
      setEditingAdditionalDecisionSupport(null);
    } else {
      const newEntry = {
        ...currentAdditionalDecisionSupport,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      setAdditionalDecisionSupportEntries(prev => [...prev, newEntry]);
    }
    
    setCurrentAdditionalDecisionSupport(getDefaultAdditionalDecisionSupport());
  };

  const editAdditionalDecisionSupport = (entry) => {
    setCurrentAdditionalDecisionSupport(entry);
    setEditingAdditionalDecisionSupport(entry);
  };

  const cancelEditAdditionalDecisionSupport = () => {
    setCurrentAdditionalDecisionSupport(getDefaultAdditionalDecisionSupport());
    setEditingAdditionalDecisionSupport(null);
  };

  const deleteAdditionalDecisionSupport = (entryId) => {
    if (window.confirm('Are you sure you want to delete this additional decision support entry?')) {
      setAdditionalDecisionSupportEntries(prev => prev.filter(d => d.id !== entryId));
    }
  };

  return {
    // State
    additionalDecisionSupportEntries,
    setAdditionalDecisionSupportEntries,
    currentAdditionalDecisionSupport,
    setCurrentAdditionalDecisionSupport,
    editingAdditionalDecisionSupport,
    setEditingAdditionalDecisionSupport,
    // Functions
    addAdditionalDecisionSupport,
    editAdditionalDecisionSupport,
    cancelEditAdditionalDecisionSupport,
    deleteAdditionalDecisionSupport,
    getDefaultAdditionalDecisionSupport,
    updateCurrentThreatSummary,
    updateCriticalResourceNeeds
  };
};

export default useAdditionalDecisionSupport;
